import { SelfServiceBuyerForm, SelfServiceForm } from "@/schema/self-service.schema";
import { api } from "@/utils/axiosInterceptor";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  GoogleGenAI,
  createUserContent,
  createPartFromUri,
} from '@google/genai';
import { geminiResponseSchema } from "@/constants/geminiOcrResponseSchema";

const API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const ai = new GoogleGenAI({ apiKey: API_KEY });

export const useCheckSubmissionStatus = (code?: string) => {
  return useQuery({
    queryKey: ["selfServiceStatus", code],
    queryFn: async () => {
      const response = await api.get("/self-service/status-check", {
        params: { code },
      });
      return response.data as {
        statusDisplay: "Processing" | "Valid" | "Invalid";
        status: string | null;
        statusCheckCode: string;
        supplierName: string;
        totalPayableAmount: number;
        originalInvoiceNumber: string | null;
        invoiceId: string;
        submittedAt: string | null;
        validatedAt: string | null;
        errorDetails: any;
      };
    },
    enabled: !!(code && code.length === 8),
    refetchInterval: (query) => {
      // Auto-refresh every 15 seconds while still processing
      const data = query.state.data;
      return data && data.statusDisplay === "Processing" ? 15000 : false;
    },
  });
};

export const useSelfService = (id?: string, businessId?: string | null, branchId?: string | null) => {
  const { data: invoice, isLoading, isLoadingError } = useQuery({
    queryKey: ["selfServiceInvoice", id],
    queryFn: async () => {
      const response = await api.get<SelfServiceBuyerForm>(
        `/self-service/invoice/${id}`
      );
      return response.data;
    },
    enabled: !!(id && id !== "" && id !== undefined),
  });

  const findSelfServiceInvoiceMutation = useMutation({
    mutationFn: (data: SelfServiceForm) => api.post<{id: string}>("/self-service/invoice", data)    
  })

  const editBuyerDetailsMutation = useMutation({
    mutationFn: (data: {
      id: string,
      formData: SelfServiceBuyerForm 
    }) => api.put(`/self-service/invoice/${data.id}`, data.formData),
  });

  const performOcr = async (image: File) => {
    try {
      const allowed = ['image/png', 'image/jpeg'];
      if (!allowed.includes(image.type)) throw new Error(JSON.stringify({ error: { message: "Unsupported file type" } }));

      const uploaded = await ai.files.upload({
        file: image,
        config: {
          mimeType: image.type
        },
      });

      if (!uploaded.uri || !uploaded.mimeType) {
        throw new Error(JSON.stringify({ error: { message: "Failed to upload image or retrieve URI." } }));;
      }

      const res = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: createUserContent([
          createPartFromUri(uploaded.uri, uploaded.mimeType),
          `Provided a receipt, analyze it and return it strictly based on the JSON schema, for company registration, ignore value in bracket.
          Use "Tax Id" for company registration if applicable.
          Remove company registration number if not found.
          Carefully differentiate unit price and amount price.
          Carefully differentiate SST ID & company registration number.
          Carefully differentiate service tax and service charge
          Recalculate only item tax type, amount & rate based on the ratio of the price if applicable.
          Charge fee and discount outside items should put in additional fee / discount amount.
          Make the rate in maximum 2 decimal place, in percentage.
          Tax for liquor should be 8%, normal food is 6% if applicable.
          Original Invoice Number should use invoice number.
          Give the tax code base on the following
          [
            {
              "Code": "01",
              "Description": "Sales Tax"
            },
            {
              "Code": "02",
              "Description": "Service Tax"
            },
            {
              "Code": "03",
              "Description": "Tourism Tax"
            },
            {
              "Code": "04",
              "Description": "High-Value Goods Tax"
            },
            {
              "Code": "05",
              "Description": "Sales Tax on Low Value Goods"
            },
            {
              "Code": "06",
              "Description": "Not Applicable"
            },
            {
              "Code": "E",
              "Description": "Tax exemption (where applicable)"
            }
          ]
        `,
        ]),
        config: {
          responseMimeType: 'application/json',
          responseSchema: geminiResponseSchema,
        }
      })
      
      if(!res.text) {
        return
      }

      return JSON.parse(res.text)
    } catch (error: any) {
      console.error(error.message)

      throw new Error(JSON.parse(error.message).error.message)
    }
  }

  const createInvoiceSelfServiceOcr = useMutation({
    mutationFn: (data: any) => api.post('/self-service/invoice-ocr', data)
  })

  const {
    data: selfServiceBusinessAndBranch,
    isLoading: selfServiceBusinessAndBranchIsLoading,
    isError: selfServiceBusinessAndBranchIsError
  } = useQuery({
    queryKey: ["selfServiceBusinessAndBranch", businessId, branchId],
    queryFn: async () => {
      const response = await api.get(
        `/self-service/business-and-branch-details`,
        {
          params: {
            businessId: businessId || undefined,
            branchId: branchId || undefined
          }
        }
      );
      return response.data;
    },
    enabled: !!(businessId && branchId && businessId !== "null" && branchId !== "null")
  })

  return {
    invoice,
    isLoading,
    isLoadingError,
    findSelfServiceInvoiceAsync: findSelfServiceInvoiceMutation.mutateAsync,
    isSearchingInvoice: findSelfServiceInvoiceMutation.isPending,
    editBuyerDetailsAsync: editBuyerDetailsMutation.mutateAsync,
    isSubmittingBuyerDetails: editBuyerDetailsMutation.isPending,
    performOcr,
    createInvoicSelfServiceOcr: createInvoiceSelfServiceOcr.mutateAsync,
    selfServiceBusinessAndBranch,
    selfServiceBusinessAndBranchIsLoading,
    selfServiceBusinessAndBranchIsError
  };
};
